/**
 * Admin warning: an imported stub and the app's own stub for the same week disagree
 * (over $1 or 0.1 h). Pops up when Payroll opens with unresolved items and after an import
 * finds new ones; each item can be dismissed. ?discrepancy=<id> in the URL opens it too.
 */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, X, Check, Columns2, List } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import StubCompare from "./StubCompare";
import ImportedStubCard from "./ImportedStubCard";
import KnitPaystubView from "./KnitPaystubView";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DiscrepancyDialog({ refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const wanted = params.get("discrepancy");
  const [compare, setCompare] = useState({});          // disc_id -> true when showing side by side
  const [viewApp, setViewApp] = useState(null);        // paystub_id open in the full app stub view
  const [viewImport, setViewImport] = useState(null);  // import_id open in the imported stub card
  const overlayOpen = Boolean(viewApp || viewImport);
  const showCompare = (d) => compare[d.disc_id] ?? d.disc_id === wanted;

  const load = useCallback(async (show) => {
    try {
      const r = await api.get("/payroll/discrepancies", { params: { status: "open" } });
      setRows(r.data.discrepancies);
      if (show && r.data.discrepancies.length) setOpen(true);
    } catch { /* not an admin */ }
  }, []);
  useEffect(() => { load(true); }, [load, refreshKey]);
  useEffect(() => { if (wanted) setOpen(true); }, [wanted]);

  const dismiss = async (d) => {
    await api.post(`/payroll/discrepancies/${d.disc_id}/dismiss`);
    setRows((rs) => rs.filter((x) => x.disc_id !== d.disc_id));
    toast.success(`Dismissed — ${d.employee_name}, week of ${d.week_start}`);
  };
  const resolve = async (d, choice) => {
    try {
      await api.post(`/payroll/discrepancies/${d.disc_id}/resolve`, { choice });
      setRows((rs) => rs.filter((x) => x.disc_id !== d.disc_id));
      toast.success(choice === "knit" ? `${d.employee_name}'s stub for ${d.week_start} now uses the imported numbers` : `Kept the app stub for ${d.employee_name}, ${d.week_start} — totals corrected`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resolve"); }
  };
  const rescan = async () => {
    setBusy(true);
    try { const r = await api.post("/payroll/discrepancies/scan"); toast.success(`Rescan done — ${r.data.new} new, ${r.data.open} open`); await load(false); }
    finally { setBusy(false); }
  };
  const close = () => { setOpen(false); if (wanted) { const p = new URLSearchParams(params); p.delete("discrepancy"); setParams(p, { replace: true }); } };

  return (
    <Dialog open={open && !overlayOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-white text-gray-800 sm:max-w-3xl" data-testid="discrepancy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Paystub discrepancies ({rows.length})</DialogTitle>
          <DialogDescription className="text-gray-500">
            The imported stub and the app's own stub for the same week don't match (over $1 or 0.1 h). Payroll totals and T4s use the imported stub; check which one is right.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.length === 0 && <p className="py-6 text-center font-mono text-xs text-gray-500">All clear — no open discrepancies.</p>}
          {rows.map((d) => (
            <div key={d.disc_id} className={`rounded-lg border p-3 ${d.disc_id === wanted ? "border-amber-400 bg-amber-50" : "border-gray-200 bg-gray-50"}`} data-testid={`discrepancy-${d.disc_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold">{d.employee_name} <span className="font-mono text-xs font-normal text-gray-500">· week of {d.week_start}</span></p>
                  <p className="font-mono text-[10px] text-gray-500">Imported period {d.period}{d.pay_date ? ` · paid ${d.pay_date}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button type="button" onClick={() => setCompare((c) => ({ ...c, [d.disc_id]: !showCompare(d) }))} data-testid={`discrepancy-compare-${d.disc_id}`}
                    title={showCompare(d) ? "Show only the lines that differ" : "Show both stubs side by side, every line"}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-100">
                    {showCompare(d) ? <><List className="h-3 w-3" /> Differences only</> : <><Columns2 className="h-3 w-3" /> Side by side</>}
                  </button>
                  <button type="button" onClick={() => dismiss(d)} data-testid={`discrepancy-dismiss-${d.disc_id}`} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-100"><X className="h-3 w-3" /> Dismiss</button>
                </div>
              </div>
              {showCompare(d) ? (
                <StubCompare discId={d.disc_id} onOpenApp={setViewApp} onOpenImported={setViewImport} onPick={(choice) => resolve(d, choice)} />
              ) : (
              <table className="mt-2 w-full text-xs">
                <thead><tr className="font-mono text-[9px] uppercase tracking-widest text-gray-500"><th className="py-1 text-left">Field</th><th className="text-right">App stub</th><th className="text-right">Imported stub</th><th className="text-right">Difference</th></tr></thead>
                <tbody>{d.diffs.map((x) => (
                  <tr key={x.field} className="border-t border-gray-200 font-mono tabular-nums">
                    <td className="py-1 text-left font-sans">{x.label}</td>
                    <td className="text-right">{x.field === "regular_hours" ? x.app.toFixed(2) : money(x.app)}</td>
                    <td className="text-right">{x.field === "regular_hours" ? x.imported.toFixed(2) : money(x.imported)}</td>
                    <td className={`text-right font-bold ${x.diff > 0 ? "text-emerald-700" : "text-red-600"}`}>{x.diff > 0 ? "+" : ""}{x.field === "regular_hours" ? x.diff.toFixed(2) : money(x.diff)}</td>
                  </tr>
                ))}</tbody>
              </table>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => resolve(d, "knit")} data-testid={`discrepancy-use-knit-${d.disc_id}`} className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#1a3fd1]/90"><Check className="h-3 w-3" /> Use the imported numbers</button>
                <button type="button" onClick={() => resolve(d, "app")} data-testid={`discrepancy-keep-app-${d.disc_id}`} className="inline-flex items-center gap-1 rounded-md border border-[#1a3fd1] px-2.5 py-1 text-[11px] font-semibold text-[#1a3fd1] hover:bg-blue-50"><Check className="h-3 w-3" /> Keep the app stub</button>
                <span className="font-mono text-[10px] text-gray-500">Imported = rewrites the app stub · App = year totals corrected by the difference</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-1">
          <button type="button" onClick={rescan} disabled={busy} data-testid="discrepancy-rescan" className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-gray-900"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Rescan all imported stubs</button>
          <button type="button" onClick={close} data-testid="discrepancy-close" className="rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white">Close</button>
        </div>
      </DialogContent>
      {/* Full stub views: the dialog steps aside while one is open (its focus trap would
          steal typing from the CPP2 box) and comes straight back when it closes. */}
      {viewImport && createPortal(
        <div data-testid="discrepancy-imported-view"><ImportedStubCard importId={viewImport} onClose={() => setViewImport(null)} /></div>, document.body)}
      {viewApp && createPortal(
        <div data-testid="discrepancy-app-view"><KnitPaystubView paystubId={viewApp} onClose={() => setViewApp(null)} /></div>, document.body)}
    </Dialog>
  );
}
